import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Calendar, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBooking } from '../hooks/useBooking';

const BookingModal = ({ property, onClose }) => {
  const { mutate: createBooking, isPending } = useBooking();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const pricePerNight = parseInt(property.price.replace(/[^0-9]/g, '')) || 100;

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const isDateBooked = (dateStr) => property.bookedDates.includes(dateStr);

  const getDaysArray = (month) => {
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push(dateStr);
    }
    return days;
  };

  const handleDateClick = (dateStr) => {
    if (isDateBooked(dateStr)) return;
    
    if (!checkInDate) {
      setCheckInDate(dateStr);
    } else if (!checkOutDate) {
      if (dateStr > checkInDate) {
        if (hasBookedDatesInRange(checkInDate, dateStr)) {
          toast.error('Selected dates contain blocked dates. Please choose different dates.');
          return;
        }
        setCheckOutDate(dateStr);
      } else {
        setCheckInDate(dateStr);
        setCheckOutDate('');
      }
    } else {
      setCheckInDate(dateStr);
      setCheckOutDate('');
    }
  };

  const isDateInRange = (dateStr) => {
    if (!checkInDate || !checkOutDate) return false;
    return dateStr > checkInDate && dateStr < checkOutDate;
  };

  const isDateDisabled = (dateStr) => {
    // Use UTC date to avoid timezone issues
    const today = new Date();
    const todayUTC = new Date(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date < todayUTC || isDateBooked(dateStr);
  };

  const hasBookedDatesInRange = (startDate, endDate) => {
    // Parse dates properly to avoid timezone issues
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    
    let currentDate = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);

    while (currentDate < end) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      if (property.bookedDates.includes(dateStr)) {
        return true;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return false;
  };

  // Calculate nights
  const calculateNights = () => {
    if (!checkInDate || !checkOutDate) return 0;
    
    // Parse dates as local dates to avoid timezone shift
    const [checkInYear, checkInMonth, checkInDay] = checkInDate.split('-').map(Number);
    const [checkOutYear, checkOutMonth, checkOutDay] = checkOutDate.split('-').map(Number);
    
    const checkIn = new Date(checkInYear, checkInMonth - 1, checkInDay);
    const checkOut = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);
    
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const nights = calculateNights();
  const totalPrice = nights * pricePerNight;

  // Validate dates
  const validateDates = () => {
    const newErrors = {};

    if (!checkInDate) newErrors.checkInDate = 'Check-in date is required';
    if (!checkOutDate) newErrors.checkOutDate = 'Check-out date is required';

    if (checkInDate && checkOutDate) {
      // Parse dates properly to avoid timezone issues
      const [checkInYear, checkInMonth, checkInDay] = checkInDate.split('-').map(Number);
      const [checkOutYear, checkOutMonth, checkOutDay] = checkOutDate.split('-').map(Number);
      
      let currentDate = new Date(checkInYear, checkInMonth - 1, checkInDay);
      const checkOut = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);

      while (currentDate < checkOut) {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        if (property.bookedDates.includes(dateStr)) {
          newErrors.dateOverlap = 'Selected dates overlap with booked dates';
          break;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (validateDates()) {
        setStep(2);
      }
    } else if (step === 2) {
      if (validateForm()) {
        setStep(3);
      }
    }
  };

  const handleProceedToPayment = () => {
    const payload = {
      property_id: property.id,
      check_in_date: checkInDate,
      check_out_date: checkOutDate,
      type: 'rent',
      customer: {
        email: formData.email,
        name: formData.fullName,
        phone: formData.phone,
        address: formData.address
      }
    };

    createBooking(payload, {
      onSuccess: (bookingData) => {
        toast.success('Redirecting to payment...');
        // Redirect to Stripe checkout URL
        window.location.href = bookingData;
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create booking. Please try again.');
      }
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg sm:text-2xl font-display font-bold text-prose-black">
            {step === 1 ? 'Select Dates' : step === 2 ? 'Confirm Booking' : 'Payment'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 md:p-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-prose-brand/10 p-4 rounded-lg">
                <p className="text-xs sm:text-sm text-prose-brand font-semibold">
                  Property: {property.title}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Price per night: £{Math.round(pricePerNight)}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-prose-black">
                    {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-2 hover:bg-gray-100 rounded transition-all"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-2 hover:bg-gray-100 rounded transition-all"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {getDaysArray(currentMonth).map((date, idx) => {
                    if (!date) return <div key={`empty-${idx}`}></div>;
                    const booked = isDateBooked(date);
                    const disabled = isDateDisabled(date);
                    const isCheckIn = date === checkInDate;
                    const isCheckOut = date === checkOutDate;
                    const inRange = isDateInRange(date);

                    return (
                      <button
                        key={date}
                        onClick={() => handleDateClick(date)}
                        disabled={disabled}
                        className={`aspect-square text-xs font-semibold rounded transition-all ${
                          booked
                            ? 'bg-red-200 text-red-700 cursor-not-allowed font-bold'
                            : disabled
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : isCheckIn || isCheckOut
                            ? 'bg-prose-brand text-white'
                            : inRange
                            ? 'bg-prose-brand/30 text-prose-black'
                            : 'bg-gray-100 text-prose-black hover:bg-prose-brand/20 cursor-pointer'
                        }`}
                        title={booked ? 'Booked' : ''}
                      >
                        {new Date(date).getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 mb-3">
                  <strong>Legend:</strong> 
                  <span className="ml-2 inline-block w-3 h-3 bg-red-200 rounded mr-1"></span>Booked
                  <span className="ml-3 inline-block w-3 h-3 bg-prose-brand rounded mr-1"></span>Selected
                  <span className="ml-3 inline-block w-3 h-3 bg-prose-brand/30 rounded mr-1"></span>In Range
                </p>
                {checkInDate && checkOutDate && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Check-in:</strong> {checkInDate}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Check-out:</strong> {checkOutDate}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Nights:</strong> {nights}
                    </p>
                    <p className="text-lg font-bold text-prose-brand mt-2">
                      Total: £{totalPrice.toFixed(2)}
                    </p>
                    <button 
                      onClick={() => {
                        setCheckInDate('');
                        setCheckOutDate('');
                      }}
                      className="mt-4 w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm"
                    >
                      Clear & Start Over
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-prose-brand/10 p-4 rounded-lg">
                <h3 className="font-semibold text-prose-black mb-3">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="text-gray-600">Property:</span>
                    <span className="font-semibold text-prose-black ml-2">{property.title}</span>
                  </p>
                  <p>
                    <span className="text-gray-600">Check-in:</span>
                    <span className="font-semibold text-prose-black ml-2">{checkInDate}</span>
                  </p>
                  <p>
                    <span className="text-gray-600">Check-out:</span>
                    <span className="font-semibold text-prose-black ml-2">{checkOutDate}</span>
                  </p>
                  <p>
                    <span className="text-gray-600">Nights:</span>
                    <span className="font-semibold text-prose-black ml-2">{nights}</span>
                  </p>
                  <div className="border-t border-prose-brand/20 mt-3 pt-3">
                    <p className="text-lg">
                      <span className="text-gray-600">Total Price:</span>
                      <span className="font-bold text-prose-gold ml-2">£{totalPrice.toFixed(2)}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-prose-black">Your Information</h3>

                <div>
                  <label className="block text-sm font-semibold text-prose-black mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleFormChange}
                    placeholder="John Smith"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-all"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-prose-black mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-all"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-prose-black mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="07700 123456"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-all"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-prose-black mb-2">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    placeholder="123 Main Street, City, Country, Postcode"
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-all resize-none"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Calendar className="text-blue-600" size={32} />
                </div>
                <h3 className="text-lg font-bold text-prose-black mb-2">Booking In Progress</h3>
                <p className="text-gray-600 mb-4">
                  Review your booking details before proceeding to payment
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{property.title}</span>
                  <span className="font-semibold">{nights} nights</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{checkInDate} to {checkOutDate}</span>
                  <span className="font-semibold">£{Math.round(pricePerNight)}/night</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between">
                  <span className="font-semibold text-prose-black">Total Amount</span>
                  <span className="text-xl font-bold text-prose-gold">£{totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <div className="bg-prose-brand/10 border border-prose-brand/30 p-4 rounded-lg">
                <p className="text-sm">
                  <strong>Booking Name:</strong> {formData.fullName}
                </p>
                <p className="text-sm mt-1">
                  <strong>Email:</strong> {formData.email}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-between">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-2 px-6 py-3 border border-gray-200 text-prose-black rounded-lg font-semibold hover:bg-gray-100 transition-all"
            >
              <ChevronLeft size={18} /> Back
            </button>
          )}
          {step === 1 && (
            <button
              onClick={handleNextStep}
              disabled={!checkInDate || !checkOutDate}
              className="ml-auto flex items-center gap-2 px-6 py-3 bg-prose-brand text-white rounded-lg font-semibold hover:bg-prose-brand/90 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={18} />
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleNextStep}
              className="ml-auto flex items-center gap-2 px-6 py-3 bg-prose-brand text-white rounded-lg font-semibold hover:bg-prose-brand/90 hover:scale-105 transition-all duration-300"
            >
              Continue to Payment <ChevronRight size={18} />
            </button>
          )}

          {step === 3 && (
            <button
              onClick={handleProceedToPayment}
              disabled={isPending}
              className="ml-auto px-6 py-3 bg-prose-gold text-prose-black rounded-lg font-semibold hover:bg-prose-gold/90 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isPending ? 'Processing...' : 'Proceed to Payment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
